import { useState, useEffect } from 'react';
import moment from 'moment';
import { useBreakpointValue } from '@chakra-ui/react';
import {
  Button,
  FormControl,
  Heading,
  Input,
  VStack,
} from 'components/ui';
import { useNavigate } from 'react-router-dom';
import { useUserRegisterPut } from '../../api/user/mutations/useUserRegisterPut';
import { useAuthenticatedUser } from '../../api/user/useAuthenticatedUser';
import { TwoColsLayout } from '../TwoColsLayout';
import { useTracking } from '../../hooks/useTracking';
import {
  DESKTOP_BUTTON_WIDTH_PX,
  TRACKING_CLICK_UNSUBSCRIBED_LANDING_CTA,
  TRACKING_VISITED_UNSUBSCRIBED_LANDING,
} from '../OnboardingForm/constants';
import { LoadingScreen } from '../ui/LoadingScreen';
import { TestimonialWithContactSupportCard } from '../OnboardingForm/TestimonialWithContactSupportCard';
import HeaderNav from '../HeaderNav/HeaderNav';
import { BirthdayInput } from '../OnboardingForm/components/BirthdayInput/BirthdayInput';
import { useBirthdayInput } from '../OnboardingForm/components/BirthdayInput/useBirthdayInput';

const UnsubscribedLanding = () => {
  const { page: pageView, track } = useTracking();

  const { birthdayError, handleBirthdayChange, validateBirthday } =
    useBirthdayInput();

  const navigate = useNavigate();
  const [firstName, setFirstName] = useState();
  const [lastName, setLastName] = useState();
  const [location, setLocation] = useState();
  const [birthdate, setBirthdate] = useState();

  const isMobile = useBreakpointValue({
    base: true,
    lg: false,
  });

  const { mutation: updateUserRegisterMutation } = useUserRegisterPut();

  const { user: authenticatedUser, isLoading: isUserLoading } =
    useAuthenticatedUser();

  // Effect to auto populate form fields. These can be changed, which is
  // why we need to take the React Query response and set it to local state
  useEffect(() => {
    if (!authenticatedUser) return;

    setFirstName(authenticatedUser.first_name);
    setLastName(authenticatedUser.last_name);
    setLocation({
      value: authenticatedUser.address_state,
      label: authenticatedUser.address_state,
    });
    if (authenticatedUser?.birthdate) {
      setBirthdate(moment(authenticatedUser.birthdate).format('MM/DD/YYYY'));
    }
  }, [authenticatedUser]);

  useEffect(() => {
    pageView(TRACKING_VISITED_UNSUBSCRIBED_LANDING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async () => {
    if (!validateBirthday(birthdate || '')) {
      return;
    }

    track(TRACKING_CLICK_UNSUBSCRIBED_LANDING_CTA);

    try {
      await updateUserRegisterMutation.mutateAsync({
        firstName,
        lastName,
        state: location.value,
        birthday: birthdate,
      });

      navigate('/health-plan', {
        state: {
          userId: authenticatedUser.id,
          email: authenticatedUser.email,
          firstName,
          lastName,
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  if (isUserLoading || updateUserRegisterMutation.isLoading) {
    return <LoadingScreen />;
  }

  const renderContent = () => {
    return (
      <TwoColsLayout
        showHeader={!isMobile}
        leftChildren={
          <VStack flex={1} pt={isMobile ? undefined : 8}>
            <Heading textStyle="heading.h2" textAlign={'left'}>
              You’re almost there - let’s check if Ayble is covered for you.
            </Heading>

            <FormControl mt={8} mx="auto" zIndex={1}>
              <FormControl.Label>Email address</FormControl.Label>
              <Input
                disabled={true}
                maxW={isMobile ? '100%' : DESKTOP_BUTTON_WIDTH_PX}
                type="text"
                value={authenticatedUser?.email}
                w={'100%'}
              />

              <FormControl.Label>First Name</FormControl.Label>
              <Input
                maxW={isMobile ? '100%' : DESKTOP_BUTTON_WIDTH_PX}
                onChange={(e) => setFirstName(e.target.value)}
                type="text"
                value={firstName}
                w={'100%'}
              />

              <FormControl.Label>Last Name</FormControl.Label>
              <Input
                maxW={isMobile ? '100%' : DESKTOP_BUTTON_WIDTH_PX}
                onChange={(e) => setLastName(e.target.value)}
                type="text"
                value={lastName}
                w={'100%'}
              />
            </FormControl>

            {!!authenticatedUser?.birthdate && (
              <BirthdayInput
                birthdayError={birthdayError}
                birthday={birthdate}
                handleBirthdayChange={(value) => {
                  const formattedValue = handleBirthdayChange(value);
                  setBirthdate(formattedValue);
                }}
              />
            )}

            <Button
              disabled={!birthdate || birthdayError}
              maxW={isMobile ? undefined : DESKTOP_BUTTON_WIDTH_PX}
              mb={5}
              mt={9}
              onClick={onSubmit}
              w={'100%'}
            >
              Continue
            </Button>
          </VStack>
        }
        rightChildren={<TestimonialWithContactSupportCard />}
      />
    );
  };

  if (isMobile) {
    return (
      <VStack flex={1}>
        <HeaderNav />

        <VStack alignItems={'center'} overflow="auto" p={'24px'}>
          {renderContent()}
        </VStack>
      </VStack>
    );
  }

  return renderContent();
};

export default UnsubscribedLanding;
